/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { useContext, useState } from 'react';

import { Button, Grid, IconButton, PageNotification, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { EditIcon } from '@coursera/cds-icons';
import FullscreenDialog from '@coursera/event-pulse/react/FullscreenDialog';

import { PortfolioBuilderContext } from 'bundles/account-profile/components/recruiting-profile-viewer/PortfolioBuilderContextProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ViewProjectDialogDetails from 'bundles/account-profile/components/recruiting-profile-viewer/components/projects/ViewProjectDialogDetails';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import type { SharedLearnerProfile_PortfolioProjectsFragment as PortfolioProject } from 'bundles/account-profile/components/recruiting-profile-viewer/queries/__generated__/SharedLearnerProfile_portfolioProjects';
import { getViewProjectDialogTranslations } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/projectUtils';
import { useEnableProjectEnhancements } from 'bundles/employability-common/feature-flags/hooks/useEnableProjectEnhancements';

import _t from 'i18n!nls/account-profile';

const styles = {
  withFlexEnd: css`
    display: flex;
    justify-content: flex-end;
  `,
  modalContent: css`
    overflow-y: scroll;

    /* There were some native CDS styles overriding this, so I wrapped it in
    .down('xl') to increase specificity without having to use !important */
    ${breakpoints.down('xl')} {
      padding-top: var(--cds-spacing-600);
      padding-bottom: var(--cds-spacing-600);
    }
  `,
  headerSlot: css`
    ${breakpoints.down('xs')} {
      /* Without this styling the header text doesn't wrap and moves off the page for smaller screen sizes */
      .cds-FullscreenDialogHeader-slot1 {
        display: contents;
      }
    }
  `,
  titleLine: css`
    margin-bottom: var(--cds-spacing-300);
    margin-top: var(--cds-spacing-300);
  `,
  notification: css`
    margin-bottom: var(--cds-spacing-200);
  `,
};

type PropsFromCaller = {
  initialCompletedProject: PortfolioProject;
  completedPortfolioProjects: PortfolioProject[];
  isOpen: boolean;
  onClose: () => void;
};

const ViewProjectsDialog = ({
  initialCompletedProject,
  isOpen,
  onClose,
  completedPortfolioProjects,
}: PropsFromCaller) => {
  const translations = getViewProjectDialogTranslations();

  const { profileState = {}, editable, sharedEventingV3Data } = useContext(ProfileContext);
  const { openPortfolioBuilder } = useContext(PortfolioBuilderContext);
  const enableProjectEnhancements = useEnableProjectEnhancements();

  const [currentProject, setCurrentProject] = useState(initialCompletedProject);
  const { fullName = '' } = profileState;

  const { Header, Content } = FullscreenDialog;
  const { title } = currentProject;

  const getIndexOfCurrentProject = () => {
    return completedPortfolioProjects.findIndex((project) => project.title === currentProject.title);
  };

  const loadNextProject = () => {
    const currentIndex = getIndexOfCurrentProject();
    const nextIndex = currentIndex + 1;

    if (nextIndex < completedPortfolioProjects.length) {
      const nextProject = completedPortfolioProjects[nextIndex];
      setCurrentProject(nextProject);
    }
  };

  const loadPreviousProject = () => {
    const currentIndex = getIndexOfCurrentProject();
    const previousIndex = currentIndex - 1;
    if (previousIndex >= 0) {
      const previousProject = completedPortfolioProjects[previousIndex];
      setCurrentProject(previousProject);
    }
  };

  const handleEditProjectClick = () => {
    openPortfolioBuilder({
      project: currentProject,
      isPublished: true,
    });
    onClose();
  };

  const showProjectEnhancements = enableProjectEnhancements && editable;

  return (
    <FullscreenDialog
      key={currentProject.id}
      trackingData={{
        modal: {
          name: EVENTS.portfolioViewProjectsDialog,
        },
        ...sharedEventingV3Data,
      }}
      open={isOpen}
      onClose={onClose}
      sticky="actions"
    >
      <Header
        css={styles.headerSlot}
        slot1={
          <div>
            <Typography variant="h2semibold">{title}</Typography>
            {fullName && (
              <Typography2 component="p" variant="bodySecondary" color="supportText">
                {_t('Project by #{fullName}', { fullName })}
              </Typography2>
            )}
          </div>
        }
        slot2={
          completedPortfolioProjects.length > 1 ? (
            <div css={styles.withFlexEnd}>
              <IconButton
                aria-label={_t('Previous project')}
                onClick={loadPreviousProject}
                intent="previous"
                variant="ghost"
                disabled={getIndexOfCurrentProject() === 0}
                data-testid="previous-project-button"
              />
              <IconButton
                data-testid="next-project-button"
                aria-label={_t('Next project')}
                onClick={loadNextProject}
                intent="next"
                variant="ghost"
                disabled={getIndexOfCurrentProject() === completedPortfolioProjects.length - 1}
              />
            </div>
          ) : undefined
        }
      />
      <Content css={styles.modalContent}>
        {showProjectEnhancements && (
          <PageNotification css={styles.notification} role="complementary" severity="information">
            {translations.notification}
          </PageNotification>
        )}
        <Grid css={styles.titleLine} container justifyContent="space-between">
          <Typography variant="h1semibold">{title}</Typography>

          {showProjectEnhancements && (
            <Button onClick={handleEditProjectClick} icon={<EditIcon />} iconPosition="before" variant="secondary">
              {translations.editProjectBtnLabel}
            </Button>
          )}
        </Grid>

        <ViewProjectDialogDetails currentProject={currentProject} />
      </Content>
    </FullscreenDialog>
  );
};

export default ViewProjectsDialog;
